import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { History, ChevronDown, ChevronRight } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

function Row({ u }) {
  const [open, setOpen] = useState(false);
  const applied = u.matched?.length || 0;
  return (
    <li className="py-1.5" data-testid={`bulk-mvr-history-${u.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-2 text-left">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        <span className="font-mono text-slate-500">{(u.created_at || "").slice(0, 16).replace("T", " ")}</span>
        <b>{u.customer}</b><span className="text-slate-500">pulled {u.received_at}</span>
        <span className="ml-auto inline-flex items-center gap-2">
          <span className="text-emerald-700">{applied} applied</span>
          {u.failed?.length > 0 && <span className="text-rose-700">{u.failed.length} failed</span>}
          {u.unmatched_names?.length > 0 && <span className="text-amber-700">{u.unmatched_names.length} not on roster</span>}
          {u.pending?.length > 0 && <span className="text-amber-700">{u.pending.length} awaiting assignment</span>}
          <span className="text-slate-400">{u.total_files != null ? `${u.total_files} files` : ""} · {u.billable ? "billed" : "no charge"} · {u.created_by_name || ""}</span>
        </span>
      </button>
      {open && (
        <div className="ml-6 mt-1 space-y-1">
          {u.matched?.map((m) => <div key={m.report_id} className="text-emerald-800"><span className="font-mono">{m.file}</span> → {m.driver} · MVR {m.mvr_date}{u.billable ? ` · ${money(m.total)}` : ""}</div>)}
          {u.failed?.map((f) => <div key={f.file} className="text-rose-700"><span className="font-mono">{f.file}</span> → {f.driver}: {f.error}</div>)}
          {u.unmatched_names?.map((n) => <div key={n} className="text-amber-800"><span className="font-mono">{n}</span> · no active driver matched (name/DL# not in filename or driver not on roster)</div>)}
          {u.pending?.map((p) => <div key={p.file} className="text-amber-800"><span className="font-mono">{p.file}</span> · still waiting for you to assign{p.candidates ? ` (${p.candidates} possible drivers)` : ""}</div>)}
          {!u.matched?.length && !u.failed?.length && !u.unmatched_names?.length && !u.pending?.length && <div className="text-slate-500">Details weren't recorded for this older upload.</div>}
        </div>
      )}
    </li>
  );
}

export function BulkMvrHistory({ customerId }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/drivers/bulk-mvr/history", { params: customerId ? { customer_id: customerId } : {} }).then(({ data }) => setRows(data)).catch((err) => toast.error(formatApiError(err)));
  }, [open, customerId]);
  return (
    <div className="rounded-md border border-slate-200 p-3 text-xs" data-testid="bulk-mvr-history">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="bulk-mvr-history-toggle">
        <History className="h-4 w-4 text-slate-500" /><span className="font-medium text-slate-800">Upload history</span><span className="text-slate-500">what each past bulk upload actually applied{customerId ? "" : " · all customers"}</span>
        <span className="ml-auto text-slate-400">{open ? "hide" : "show"}</span>
      </button>
      {open && <ul className="mt-2 divide-y divide-slate-100 max-h-64 overflow-y-auto">{rows === null ? <li className="py-2 text-slate-500">Loading…</li> : rows.length ? rows.map((u) => <Row key={u.id} u={u} />) : <li className="py-2 text-slate-500">No bulk uploads recorded yet.</li>}</ul>}
    </div>
  );
}
